import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { openConfirmModal, openModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconPencil, IconSitemap, IconTrash, IconX } from '@tabler/icons-react';

import { MedicalOrganization } from '@interfaces/medicalOrganizations.interface';

import { isApiError } from '@api/index';
import {
  useDeleteMedicalOrganizationMutation,
  useGetMedicalOrganizationByIdQuery,
} from '@api/medicalOrganizations.api';

import useAuth from '@hooks/useAuth';
import useLayoutProps from '@hooks/useLayoutProps';
import useUserPermissionGuard from '@hooks/useUserPermissionGuard';

import MedicalOrganizationAddressesTable from '@components/medicalOrganizationAddresses/MedicalOrganizationAddressesTable';
import UpsertMedicalOrganizationForm, {
  UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID,
} from '@components/medicalOrganizations/UpsertMedicalOrganizationForm';

export default function MedicalOrganizationDetails() {
  // ==========================================================================
  // General
  // ==========================================================================
  const { user, logout } = useAuth();

  const navigate = useNavigate();

  const { medicalOrganizationId } = useParams();

  const canReadAllMedicalOrganizations = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.READ',
    false,
  );
  const canUpdateMedicalOrganization = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.UPDATE',
  );
  const canDeleteMedicalOrganization = useUserPermissionGuard(
    'MEDICAL_ORGANIZATIONS.DELETE',
  );

  // ==========================================================================
  // Api
  // ==========================================================================
  const { data: medicalOrganization, isLoading: isLoadingMedicalOrganization } =
    useGetMedicalOrganizationByIdQuery(medicalOrganizationId!);

  const [
    deleteMedicalOrganization,
    { isLoading: isLoadingDeleteMedicalOrganization },
  ] = useDeleteMedicalOrganizationMutation();

  // ==========================================================================
  // Handlers
  // ==========================================================================
  const handleEditMedicalOrganizationClick = useCallback(
    (medicalOrganization: MedicalOrganization) => {
      openModal({
        modalId: UPSERT_MEDICAL_ORGANIZATION_FORM_MODAL_ID,
        title: 'Modifica organizzazione medica',
        children: (
          <UpsertMedicalOrganizationForm
            medicalOrganization={medicalOrganization}
          />
        ),
      });
    },
    [],
  );

  const handleDeleteMedicalOrganizationClick = useCallback((id: string) => {
    openConfirmModal({
      title: 'Elimina organizzazione medica',
      children:
        "Stai per eliminare l'organizzazione medica. Eliminerai il tuo account e non potrai più accedere. L'operazione non è reversibile.\nContinuare?",
      confirmProps: { color: 'red' },
      labels: { confirm: 'Conferma', cancel: 'Annulla' },
      onConfirm: async () => {
        try {
          await deleteMedicalOrganization(id).unwrap();

          if (user?.medicalOrganizationId === id) {
            await logout();
          }

          showNotification({
            title: 'Organizzazione medica eliminata',
            message: 'Organizzazione medica eliminata con successo',
          });

          navigate(-1);
        } catch (e) {
          if (isApiError(e)) {
            showNotification({
              title: 'Errore',
              message: e.data.message,
              color: 'red',
              icon: <IconX />,
            });
          } else {
            showNotification({
              title: 'Errore',
              message: 'Errore sconosciuto',
              color: 'red',
              icon: <IconX />,
            });
            console.error(e);
          }
        }
      },
    });
  }, []);

  // ==========================================================================
  // Render
  // ==========================================================================
  const title = useMemo(
    () => (
      <Group>
        <IconSitemap />
        {medicalOrganization ? `${medicalOrganization.user.name}` : ''}
      </Group>
    ),
    [medicalOrganization],
  );

  const backLink = useMemo(
    () =>
      canReadAllMedicalOrganizations
        ? {
            title: 'Organizzazioni mediche',
            to: '/organizzazioni-mediche',
          }
        : undefined,
    [canReadAllMedicalOrganizations],
  );

  useLayoutProps({
    title,
    backLink,
  });

  return isLoadingMedicalOrganization ? (
    <Loader />
  ) : !medicalOrganization ? (
    <Text fs="italic">Organizzazione medica non trovata o non accessibile</Text>
  ) : (
    <>
      <Paper withBorder p="md" mb="xl">
        <Group justify="space-between" align="start">
          <div>
            <Title order={4} mb="md">
              Informazioni organizzazione
            </Title>

            <Text>
              <Text fw={700} span>
                Nome:
              </Text>{' '}
              {medicalOrganization.user.name}
            </Text>
            <Text>
              <Text fw={700} span>
                Email:
              </Text>{' '}
              {medicalOrganization.user.email}
            </Text>
            <Text>
              <Text fw={700} span>
                Partita IVA:
              </Text>{' '}
              {medicalOrganization.vatNumber}
            </Text>
            <Text>
              <Text fw={700} span>
                Telefono:
              </Text>{' '}
              {medicalOrganization.phone}
            </Text>
          </div>
          <Stack>
            {canUpdateMedicalOrganization && (
              <Button
                leftSection={<IconPencil />}
                variant="outline"
                onClick={() =>
                  handleEditMedicalOrganizationClick(medicalOrganization)
                }
              >
                Modifica
              </Button>
            )}
            {canDeleteMedicalOrganization && (
              <Button
                leftSection={<IconTrash />}
                color="red"
                onClick={() =>
                  handleDeleteMedicalOrganizationClick(medicalOrganization.id)
                }
                loading={isLoadingDeleteMedicalOrganization}
              >
                Elimina
              </Button>
            )}
          </Stack>
        </Group>
      </Paper>
      <MedicalOrganizationAddressesTable
        medicalOrganization={medicalOrganization}
      />
    </>
  );
}
